import { ReactElement, useCallback } from 'react'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import NextLink from 'next/link'
import { useTranslation } from 'next-i18next'
import { SPACING_VARIABLES } from '@/theme/themeOptions/vars'
import { getUrl } from '@/helpers/uri'
import { ZINDEXES } from '@/constants/zindexes'
import { useAuthContext } from '@/context/auth'
import { SpriteIcon } from '@/assets/sprites/SpriteIcon'
import { Button } from '@/components/buttons/Button'

const BannerStyled = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isBannerVisible'
})<{ isBannerVisible?: boolean }>(({ theme, isBannerVisible }) => ({
  left: 0,
  position: 'fixed',
  width: '100%',
  top: SPACING_VARIABLES.APP_HEADER_HEIGHT,
  height: SPACING_VARIABLES.BANNER_HEIGHT,
  backgroundColor: theme.palette.grey['blueGrey'],
  display: 'flex',
  gap: '46px',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: ZINDEXES.BANNER,
  transition: 'all .25s ease-in-out',
  transform: `translateY(${isBannerVisible ? 0 : '-100%'})`,
  opacity: isBannerVisible ? 1 : 0,
  visibility: isBannerVisible ? 'visible' : 'hidden',
  pointerEvents: isBannerVisible ? 'auto' : 'none',

  [theme.breakpoints.down('sm')]: {
    padding: '0 20px',
    top: SPACING_VARIABLES.MOBILE_APP_HEADER_HEIGHT,
    flexDirection: 'column',
    gap: '10px'
  }
}))

const BannerButton = styled(Button)(({ theme }) => ({
  padding: `7px ${theme.spacing(3)}`,
  transition: '.3s color, .3s background, .3s border, .3s box-shadow',
  background: theme.palette.common.white,
  color: theme.palette.primary[900],
  fontSize: '15px',

  '&:hover': {
    boxShadow: '0 2px 8px rgba(0,0,0,.2), inset 0 0 0 transparent',
    background: theme.palette.common.white,
    color: theme.palette.primary[900]
  },

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    fontSize: '13px'
  }
}))

export const Banner = (): ReactElement => {
  const { isBannerDisplayed, setIsBannerDisplayed } = useAuthContext()
  const { t } = useTranslation('header')

  const onClose = useCallback(() => {
    setIsBannerDisplayed(false)
  }, [])

  return (
    <BannerStyled isBannerVisible={isBannerDisplayed}>
      <Button
        onClick={onClose}
        sx={{
          top: 10,
          right: 20,
          position: 'absolute',
          cursor: 'pointer',
          height: '40px',
          minWidth: '40px'
        }}
      >
        <SpriteIcon spriteId="times-light" color="common.white" />
      </Button>
      <Typography color="white" component="span" sx={{ typography: { xs: 'titleLight', md: 'h5' } }}>
        {t('setUpYourSimulation', 'Set up your own cloud-native simulation in minutes.')}
      </Typography>
      <NextLink href={getUrl('/signup/')} passHref legacyBehavior prefetch={false}>
        <BannerButton variant="contained" color="secondary" qa-attribute="bannerButton">
          {t('startSimulatingNow', 'Start Simulating Now')}
        </BannerButton>
      </NextLink>
    </BannerStyled>
  )
}
